/**
 * Order Types
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, these types represent order data from backend.
 * All order operations are handled by backend Order Service.
 */

/**
 * Structured error details from backend
 */
export interface OrderErrorDetails {
  userMessage?: string;
  canRetry?: boolean;
  nextSteps?: string;
  errorCategory?: string;
  severity?: 'LOW' | 'MEDIUM' | 'HIGH';
  errorCode?: string;
  originalMessage?: string;
}

/**
 * Order error with structured details
 */
export interface OrderError {
  message: string;
  details?: OrderErrorDetails;
}

/**
 * Order interface matching backend OrderResponse
 */
export interface Order {
  platform_order_id: string;
  firebase_uid: string;
  instrument_id: string;
  instrument_symbol?: string;
  broker_order_id?: string;
  broker_account_id?: string; // Extracted from broker_order if available
  broker_type?: string; // Extracted from broker_order if available
  side: 'buy' | 'sell' | 'short' | 'cover';
  quantity: string; // Decimal string from backend
  filled_quantity: string;
  remaining_quantity: string;
  order_type: 'market' | 'limit' | 'stop' | 'stop_limit' | 'trailing_stop' | 'iceberg' | 'twap' | 'vwap';
  time_in_force?: 'day' | 'gtc' | 'ioc' | 'fok' | 'gtd';
  status: string; // Order status from backend
  priority: number;
  market_session?: string;
  price?: string; // Decimal string
  stop_price?: string; // Decimal string
  limit_price?: string; // Decimal string
  created_at: string;
  updated_at: string;
  expires_at?: string;
  broker_order?: { [key: string]: any } | null;
  fills?: Array<{ [key: string]: any }>;
  // Response metadata
  message?: string; // Success or informational message from backend
  success?: boolean; // Whether the operation was successful
}

/**
 * Order creation parameters
 */
export interface CreateOrderParams {
  instrument_id: string;
  registration_id: string; // Broker account registration ID (system UUID from BrokerAccount.id) - preferred
  broker_account_id?: string; // Deprecated: use registration_id instead. Kept for backward compatibility.
  broker_type?: string; // Deprecated: use registration_id instead. Kept for backward compatibility.
  side: 'buy' | 'sell' | 'short' | 'cover';
  quantity: string; // Decimal string
  order_type: 'market' | 'limit' | 'stop' | 'stop_limit' | 'trailing_stop' | 'iceberg' | 'twap' | 'vwap';
  price?: string; // Required for limit/stop orders
  stop_price?: string; // Required for stop orders
  time_in_force?: 'day' | 'gtc' | 'ioc' | 'fok' | 'gtd';
  client_order_id?: string;
  strategy_id?: string;
  notes?: string;
  order_tags?: object;
  routing_preferences?: object;
}

/**
 * Multi-broker order result
 */
export interface MultiBrokerOrderResult {
  successes: Order[];
  failures: Array<{
    broker_account_id: string;
    broker_name: string;
    error: string;
    errorDetails?: OrderErrorDetails;
  }>;
  totalCount: number;
  successCount: number;
  failureCount: number;
}

/**
 * Helper function to extract structured error from error payload
 */
export function extractOrderError(error: unknown): OrderError {
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const errorPayload = error as any;
    return {
      message: errorPayload.message || 'Failed to place order',
      details: errorPayload.details || undefined,
    };
  }
  
  if (error instanceof Error) {
    return {
      message: error.message,
    };
  }
  
  return {
    message: 'Failed to place order',
  };
}

/**
 * Helper function to map backend OrderResponse to Order type
 */
export function mapBackendOrderToOrder(backendOrder: {
  platform_order_id?: string;
  firebase_uid?: string;
  instrument_id?: string;
  instrument_symbol?: string;
  broker_order_id?: string;
  side?: string;
  quantity?: string;
  filled_quantity?: string;
  remaining_quantity?: string;
  order_type?: string;
  time_in_force?: string;
  status?: string;
  priority?: number;
  market_session?: string;
  price?: string;
  stop_price?: string;
  limit_price?: string;
  created_at?: string;
  updated_at?: string;
  expires_at?: string;
  broker_order?: { [key: string]: any } | null;
  fills?: Array<{ [key: string]: any }>;
  message?: string;
  success?: boolean;
}): Order | null {
  if (!backendOrder.platform_order_id || !backendOrder.instrument_id) {
    return null;
  }

  // Extract broker_account_id and broker_type from broker_order if available
  const brokerAccountId = backendOrder.broker_order?.broker_account_id || '';
  const brokerType = backendOrder.broker_order?.broker_type || '';

  return {
    platform_order_id: backendOrder.platform_order_id,
    firebase_uid: backendOrder.firebase_uid || '',
    instrument_id: backendOrder.instrument_id,
    instrument_symbol: backendOrder.instrument_symbol,
    broker_order_id: backendOrder.broker_order_id,
    broker_account_id: brokerAccountId,
    broker_type: brokerType,
    side: (backendOrder.side || 'buy') as Order['side'],
    quantity: backendOrder.quantity || '0',
    filled_quantity: backendOrder.filled_quantity || '0',
    remaining_quantity: backendOrder.remaining_quantity || '0',
    order_type: (backendOrder.order_type || 'market') as Order['order_type'],
    time_in_force: backendOrder.time_in_force as Order['time_in_force'],
    status: backendOrder.status || 'pending',
    priority: backendOrder.priority || 0,
    market_session: backendOrder.market_session,
    price: backendOrder.price,
    stop_price: backendOrder.stop_price,
    limit_price: backendOrder.limit_price,
    created_at: backendOrder.created_at || new Date().toISOString(),
    updated_at: backendOrder.updated_at || new Date().toISOString(),
    expires_at: backendOrder.expires_at,
    broker_order: backendOrder.broker_order,
    fills: backendOrder.fills,
    message: backendOrder.message,
    success: backendOrder.success,
  };
}
